/**
 * Section 5 — what the post will actually look like.
 *
 * One tab per selected platform rather than a single generic card: the whole
 * point of the preview is that the same body renders differently everywhere —
 * X truncates, Instagram leads with the image, LinkedIn hides the caption behind
 * "see more" — and a generic card would hide exactly the differences the user
 * opened the preview to check.
 *
 * Engagement figures are illustrative filler. They make the frame read as the
 * real network instead of an empty shell; nothing here is a prediction.
 */

import { useEffect, useState } from 'react';
import {
  Bookmark,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Repeat2,
  Send,
  Share2,
  ThumbsUp,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import { PLATFORM_CONFIGS } from '../../../config/platforms';
import type { PlatformId } from '../../../config/platforms';
import type { ComposerMedia, ConnectedAccount } from '../../../types/composer';
import type { TranslationKey } from '../../../i18n';
import { PlatformIcon, PlatformIconTile } from './PlatformIcon';

/** Illustrative only — see the file header. */
const SAMPLE_ENGAGEMENT = { reactions: 120, comments: 23, shares: 15 };

interface PlatformPreviewProps {
  text: string;
  media: ComposerMedia[];
  selectedPlatforms: PlatformId[];
  accounts: ConnectedAccount[];
}

export function PlatformPreview({
  text,
  media,
  selectedPlatforms,
  accounts,
}: Readonly<PlatformPreviewProps>) {
  const { t } = useTranslation();
  const [activePlatform, setActivePlatform] = useState<PlatformId | null>(
    selectedPlatforms[0] ?? null
  );

  // Deselecting the previewed platform must not leave a tab selected that is no
  // longer in the strip, so the active tab follows the selection.
  useEffect(() => {
    setActivePlatform((current) =>
      current && selectedPlatforms.includes(current) ? current : (selectedPlatforms[0] ?? null)
    );
  }, [selectedPlatforms]);

  if (selectedPlatforms.length === 0 || activePlatform === null) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center">
        <p className="text-[13px] font-semibold text-slate-700">{t('composer.preview.empty')}</p>
        <p className="mt-1 text-[11px] text-slate-500">{t('composer.preview.emptyHint')}</p>
      </div>
    );
  }

  const account = accounts.find((a) => a.platform === activePlatform) ?? null;

  return (
    <div>
      {/* Platform tabs */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {selectedPlatforms.map((platform) => {
          const isActive = platform === activePlatform;
          return (
            <button
              key={platform}
              type="button"
              onClick={() => setActivePlatform(platform)}
              aria-pressed={isActive}
              title={PLATFORM_CONFIGS[platform].name}
              className={cn(
                'flex h-9 flex-1 min-w-[44px] items-center justify-center rounded-lg border transition-colors',
                isActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
              )}
            >
              <PlatformIcon platform={platform} size={17} brandColor />
            </button>
          );
        })}
      </div>

      <PostFrame platform={activePlatform} account={account} text={text} media={media} />
    </div>
  );
}

interface PostFrameProps {
  platform: PlatformId;
  account: ConnectedAccount | null;
  text: string;
  media: ComposerMedia[];
}

function PostFrame({ platform, account, text, media }: Readonly<PostFrameProps>) {
  const { t } = useTranslation();
  const cfg = PLATFORM_CONFIGS[platform];
  const displayName = account ? `${cfg.name} ${account.name}` : cfg.name;
  const hero = media[0] ?? null;

  return (
    <article className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      {/* Author */}
      <header className="flex items-start gap-2.5 px-3 pb-2 pt-3">
        <PlatformIconTile platform={platform} size={36} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold leading-tight text-slate-900">
            {displayName}
          </p>
          <p className="text-[11px] leading-tight text-slate-500">
            {t('composer.preview.justNow')} · 🌐
          </p>
        </div>
        <MoreHorizontal className="h-4 w-4 shrink-0 text-slate-400" />
      </header>

      {/* Body */}
      <div className="px-3 pb-2.5 text-[13px] leading-relaxed text-slate-800">
        {text.trim().length > 0 ? (
          <RichText text={text} />
        ) : (
          <span className="text-slate-400">{t('composer.preview.noText')}</span>
        )}
      </div>

      {/* Media */}
      {hero && <MediaBlock media={media} />}

      {/* Engagement */}
      <div className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-slate-500">
        <span aria-hidden className="text-[13px] leading-none">
          👍❤️😮
        </span>
        <span className="tabular-nums">{SAMPLE_ENGAGEMENT.reactions}</span>
        <span className="flex-1" />
        <span className="tabular-nums">
          {t('composer.preview.comments', { count: SAMPLE_ENGAGEMENT.comments })}
        </span>
        <span className="tabular-nums">
          {t('composer.preview.shares', { count: SAMPLE_ENGAGEMENT.shares })}
        </span>
      </div>

      <ActionBar platform={platform} />
    </article>
  );
}

interface PreviewAction {
  icon: React.ComponentType<{ className?: string }>;
  labelKey: TranslationKey;
}

/** The row of buttons at the foot of a post, in each network's own vocabulary. */
function ActionBar({ platform }: Readonly<{ platform: PlatformId }>) {
  const { t } = useTranslation();

  const actions = {
    facebook: [
      { icon: ThumbsUp, labelKey: 'composer.preview.like' },
      { icon: MessageCircle, labelKey: 'composer.preview.comment' },
      { icon: Share2, labelKey: 'composer.preview.share' },
    ],
    linkedin: [
      { icon: ThumbsUp, labelKey: 'composer.preview.like' },
      { icon: MessageCircle, labelKey: 'composer.preview.comment' },
      { icon: Repeat2, labelKey: 'composer.preview.repost' },
      { icon: Send, labelKey: 'composer.preview.send' },
    ],
    instagram: [
      { icon: Heart, labelKey: 'composer.preview.like' },
      { icon: MessageCircle, labelKey: 'composer.preview.comment' },
      { icon: Send, labelKey: 'composer.preview.share' },
      { icon: Bookmark, labelKey: 'composer.preview.save' },
    ],
    threads: [
      { icon: Heart, labelKey: 'composer.preview.like' },
      { icon: MessageCircle, labelKey: 'composer.preview.reply' },
      { icon: Repeat2, labelKey: 'composer.preview.repost' },
      { icon: Send, labelKey: 'composer.preview.share' },
    ],
    x: [
      { icon: MessageCircle, labelKey: 'composer.preview.reply' },
      { icon: Repeat2, labelKey: 'composer.preview.repost' },
      { icon: Heart, labelKey: 'composer.preview.like' },
      { icon: Share2, labelKey: 'composer.preview.share' },
    ],
    youtube: [
      { icon: ThumbsUp, labelKey: 'composer.preview.like' },
      { icon: MessageCircle, labelKey: 'composer.preview.comment' },
      { icon: Share2, labelKey: 'composer.preview.share' },
    ],
    tiktok: [
      { icon: Heart, labelKey: 'composer.preview.like' },
      { icon: MessageCircle, labelKey: 'composer.preview.comment' },
      { icon: Share2, labelKey: 'composer.preview.share' },
    ],
    pinterest: [
      { icon: Heart, labelKey: 'composer.preview.like' },
      { icon: MessageCircle, labelKey: 'composer.preview.comment' },
      { icon: Bookmark, labelKey: 'composer.preview.save' },
    ],
  } as const satisfies Record<PlatformId, readonly PreviewAction[]>;

  return (
    <div className="flex items-center border-t border-slate-100">
      {actions[platform].map(({ icon: Icon, labelKey }) => (
        <span
          key={labelKey}
          className="flex flex-1 items-center justify-center gap-1.5 py-2 text-[11.5px] font-semibold text-slate-500"
        >
          <Icon className="h-3.5 w-3.5" />
          {t(labelKey)}
        </span>
      ))}
    </div>
  );
}

/** Lead image full-bleed; the rest as a strip, mirroring how feeds crop albums. */
function MediaBlock({ media }: Readonly<{ media: ComposerMedia[] }>) {
  const [hero, ...rest] = media;

  return (
    <div>
      <img
        src={hero.previewUrl}
        alt={hero.name}
        className="block max-h-[300px] w-full bg-slate-900 object-cover"
      />
      {rest.length > 0 && (
        <div className="flex gap-0.5">
          {rest.slice(0, 3).map((item) => (
            <img
              key={item.id}
              src={item.previewUrl}
              alt={item.name}
              className="h-16 flex-1 bg-slate-900 object-cover"
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Hashtags, @mentions and links render as links on every network, so the preview
 * has to colour them or the user cannot see how much of their post is link text.
 */
function RichText({ text }: Readonly<{ text: string }>) {
  const parts = text.split(/(#[\p{L}\p{N}_]+|@[\p{L}\p{N}_.]+|https?:\/\/\S+)/gu);

  return (
    <p className="whitespace-pre-wrap break-words">
      {parts.map((part, i) =>
        /^[#@]|^https?:\/\//.test(part) ? (
          <span key={`${part}-${i}`} className="text-blue-600">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </p>
  );
}
